import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RetryIcon,
  Person as UserIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import { MessageProps, SentFileAttachment } from '../../types/message';
import { MarkdownRenderer } from '../UI/MarkdownRenderer';
import { EXPERT_AVATARS } from '../../types/ai';
import { SentFileList } from './SentFileList';

/**
 * 個別メッセージ表示コンポーネント
 * ユーザーメッセージとAIメッセージを区別して表示
 */
export const MessageItem: React.FC<MessageProps> = ({
  message,
  expert,
  isTyping = false,
  isRegenerating = false,
  onRetry,
}) => {
  const theme = useTheme();
  const isUser = message.messageType === 'user';
  
  // 専門家AIのアバターを動的に取得
  const getExpertAvatar = (): string => {
    // 名前から推測してアバターを決定
    const lowercaseName = expert?.name?.toLowerCase() || '';
    if (lowercaseName.includes('不動産') || lowercaseName.includes('営業')) {
      return EXPERT_AVATARS.real_estate_sales;
    } else if (lowercaseName.includes('法務') || lowercaseName.includes('法律')) {
      return EXPERT_AVATARS.legal;
    } else if (lowercaseName.includes('税務') || lowercaseName.includes('税金')) {
      return EXPERT_AVATARS.tax;
    }
    return EXPERT_AVATARS.default;
  };
  
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ファイルダウンロード処理
  const handleFileDownload = (file: SentFileAttachment) => {
    // TODO: 実際のダウンロード機能を実装
    console.log('ファイルダウンロード:', file);
    // 将来的にはS3からのダウンロードURLを取得してダウンロードを実行
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(0, 2),
      }}
    >
      {/* アバター */}
      <Avatar
        sx={{
          width: 36,
          height: 36,
          backgroundColor: isUser ? theme.palette.primary.main : theme.palette.secondary.main,
          marginLeft: isUser ? theme.spacing(1) : 0,
          marginRight: isUser ? 0 : theme.spacing(1),
          fontSize: isUser ? '1rem' : '1.2rem',
        }}
      >
        {isUser ? (
          <UserIcon fontSize="small" />
        ) : (
          getExpertAvatar()
        )}
      </Avatar>

      {/* メッセージ内容 */}
      <Box
        sx={{
          maxWidth: { xs: '85%', sm: '80%', md: '75%' },
          minWidth: '100px',
        }}
      >
        {/* 専門家名表示 */}
        {!isUser && expert && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary, 
              mb: 0.5,
              display: 'block',
              fontWeight: 500,
            }}
          >
            {expert.name}
          </Typography>
        )}

        {/* メッセージバブル */}
        <Box
          sx={{
            backgroundColor: isUser 
              ? theme.palette.primary.main 
              : theme.palette.grey[100],
            color: isUser 
              ? theme.palette.primary.contrastText 
              : theme.palette.text.primary,
            borderRadius: theme.shape.borderRadius * 0.75,
            padding: theme.spacing(2, 2.5),
            position: 'relative',
            wordBreak: 'break-word',
            // 再生成中は薄く表示
            opacity: isRegenerating ? 0.6 : 1,
            transition: 'opacity 0.3s ease-in-out',
            ...(theme.palette.mode === 'dark' && !isUser && {
              backgroundColor: theme.palette.grey[800],
            }),
            // メッセージバブルの矢印
            '&::before': {
              content: '""',
              position: 'absolute',
              top: theme.spacing(1),
              [isUser ? 'right' : 'left']: -8,
              width: 0,
              height: 0,
              borderTop: `8px solid ${
                isUser ? theme.palette.primary.main : 
                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]
              }`,
              borderLeft: isUser ? '8px solid transparent' : 'none',
              borderRight: isUser ? 'none' : '8px solid transparent',
            },
            // 再生成中のオーバーレイ
            ...(isRegenerating && !isUser && {
              '&::after': {
                content: '"再生成中..."',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: theme.spacing(0.5, 1),
                borderRadius: theme.shape.borderRadius * 0.5,
                fontSize: '0.75rem',
                fontWeight: 500,
                zIndex: 1,
                whiteSpace: 'nowrap',
              },
            }),
          }}
        >
          {/* メッセージ内容の表示 */}
          {!isUser ? (
            <MarkdownRenderer 
              content={message.content}
              className="message-content"
            />
          ) : (
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.95rem',
                lineHeight: 1.5,
              }}
            >
              {message.content}
            </Typography>
          )}

          {/* 添付ファイルリスト表示 */}
          {message.attachedFiles && message.attachedFiles.length > 0 && (
            <SentFileList
              files={message.attachedFiles as SentFileAttachment[]}
              onDownload={handleFileDownload}
            />
          )}
        </Box>

        {/* メッセージメタ情報 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            marginTop: theme.spacing(0.5),
            gap: theme.spacing(1),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.grey[600],
              fontSize: '0.75rem',
            }}
          >
            {formatTime(message.createdAt)}
          </Typography>

          {/* 再送信ボタン（AIメッセージのみ表示） */}
          {!isUser && onRetry && (
            <Tooltip title={isRegenerating ? "再生成中..." : "メッセージを再生成"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRetry()}
                  disabled={isRegenerating}
                  sx={{
                    color: theme.palette.grey[500],
                    padding: theme.spacing(0.25),
                    '&:hover': {
                      backgroundColor: theme.palette.grey[200],
                      color: theme.palette.grey[700],
                      ...(theme.palette.mode === 'dark' && {
                        backgroundColor: theme.palette.grey[700],
                        color: theme.palette.grey[300],
                      }),
                    },
                    '&.Mui-disabled': {
                      color: theme.palette.grey[400],
                    },
                  }}
                >
                  {isRegenerating ? (
                    <CircularProgress 
                      size={16} 
                      color="inherit"
                      sx={{ 
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        },
                      }}
                    />
                  ) : (
                    <RetryIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};